import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { PatternBead } from "src/app/models/pattern/pattern-bead";
import {
  BeadSegment,
  WordChartPaletteEntry,
  WordChartRow,
} from "src/app/models/word-chart/word-chart-models";
import { WordChartService } from "src/app/services/word-chart.service";
import { TtsService } from "src/app/services/tts.service";
import { HttpService, LanguageEnum } from "src/app/services/http-service";
import {
  DEFAULT_READER_SETTINGS,
  ReaderSettings,
} from "src/app/models/reader/reader-settings";
import { Utility } from "src/app/helpers/utility";
import {
  ReaderSettingsModal,
  ReaderSettingsModalData,
  ReaderSettingsModalResult,
} from "../reader-settings-modal/reader-settings-modal";

export interface PatternReaderModalData {
  patternId?: number;
  patternName: string;
  data: PatternBead;
  isPreviewMode: boolean;
}

const PREVIEW_WORD_CHART_ROWS = 3;
const TIME_OPTIONS = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000];
const MAX_READER_PROGRESS_ENTRIES = 20;

@Component({
  selector: "pattern-reader-modal",
  templateUrl: "./pattern-reader-modal.html",
  styleUrls: ["./pattern-reader-modal.css"],
})
export class PatternReaderModal implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("legendTemplate") legendTemplate: TemplateRef<any>;
  @ViewChild("rowsContainer") rowsContainer: ElementRef<HTMLDivElement>;
  @ViewChildren("rowElement") rowElements: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren("segmentElement") segmentElements: QueryList<ElementRef<HTMLButtonElement>>;

  rows: WordChartRow[] = [];
  totalRowsCount = 0;
  legend: WordChartPaletteEntry[] = [];
  currentRowIndex = 0;
  currentSegmentIndex = -1;
  isPlaying = false;
  isPaused = false;
  waitCountdown = 0;
  ttsSupported = false;
  settings: ReaderSettings = { ...DEFAULT_READER_SETTINGS };
  timeOptions = TIME_OPTIONS;
  waitPerBeadStepMs = TIME_OPTIONS[1] - TIME_OPTIONS[0];
  isPreviewMode = false;
  previewRowLimit = PREVIEW_WORD_CHART_ROWS;
  languageOptions: Array<{ value: string; label: string; flag: string }> = [];
  timeOptionLabels: Map<number, string> = new Map();

  private keydownHandler: (event: KeyboardEvent) => void;
  private legendDialogRef: MatDialogRef<any> | null = null;
  private isSavingSettings = false;
  private hasPendingSettingsSave = false;
  private progressSaveTimer: number | null = null;
  private hasPendingInitialRestoreScroll = false;

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<PatternReaderModal>,
    @Inject(MAT_DIALOG_DATA) public data: PatternReaderModalData,
    private wordChartService: WordChartService,
    private tts: TtsService,
    public httpService: HttpService,
  ) {}

  ngOnInit(): void {
    const result = this.wordChartService.buildWordChart(this.data.data);
    this.isPreviewMode = this.data.isPreviewMode;
    this.totalRowsCount = result.rows.length;
    this.rows = this.isPreviewMode
      ? result.rows.slice(0, this.previewRowLimit)
      : result.rows;
    this.legend = result.legend.sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return a.letter.localeCompare(b.letter);
    });
    this.ttsSupported = this.tts.isSupported();

    this.settings = this.loadReaderSettings();
    this.settings.waitPerBeadMs = this.normalizeWaitPerBeadMs(this.settings.waitPerBeadMs);
    this.restoreProgressFromSettings();

    this.languageOptions = [
      { value: "en-US", label: "English", flag: "🇬🇧" },
      { value: "it-IT", label: "Italiano", flag: "🇮🇹" },
      { value: "de-DE", label: "Deutsch", flag: "🇩🇪" },
      { value: "fr-FR", label: "Français", flag: "🇫🇷" },
      { value: "es-ES", label: "Español", flag: "🇪🇸" },
      { value: "nl-NL", label: "Nederlands", flag: "🇳🇱" },
      { value: "pt-PT", label: "Português", flag: "🇵🇹" },
      { value: "pl-PL", label: "Polski", flag: "🇵🇱" },
    ];

    this.timeOptions.forEach(option => {
      const seconds = option / 1000;
      this.timeOptionLabels.set(option, `${seconds}s`);
    });

    this.keydownHandler = (event) => this.onKeyDown(event);
    window.addEventListener("keydown", this.keydownHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener("keydown", this.keydownHandler);
    this.tts.stop();
    this.legendDialogRef?.close();
    this.flushProgressSave();
  }

  ngAfterViewInit(): void {
    if (!this.hasPendingInitialRestoreScroll) return;
    setTimeout(() => {
      this.scrollToCurrentRow();
      this.hasPendingInitialRestoreScroll = false;
    }, 0);
  }

  openLegend(): void {
    if (this.legendDialogRef) {
      return;
    }
    this.legendDialogRef = this.dialog.open(this.legendTemplate, {
      width: "720px",
      maxWidth: "95vw",
    });
    this.legendDialogRef.afterClosed().subscribe(() => {
      this.legendDialogRef = null;
    });
  }

  closeLegend(): void {
    if (this.legendDialogRef) {
      this.legendDialogRef.close();
    }
  }

  openSettings(): void {
    const data: ReaderSettingsModalData = {
      languageCode: this.currentLanguageCode,
      waitPerBeadMs: this.settings.waitPerBeadMs,
      languageOptions: this.languageOptions,
      timeOptions: this.timeOptions,
    };

    const modalRef = this.dialog.open(ReaderSettingsModal, {
      width: "fit-content",
      maxWidth: "95vw",
      panelClass: "reader-settings-dialog-panel",
      data,
    });

    modalRef.afterClosed().subscribe((result?: ReaderSettingsModalResult) => {
      if (!result) return;

      const newWaitPerBeadMs = this.normalizeWaitPerBeadMs(result.waitPerBeadMs);
      const languageChanged = this.settings.languageCode !== result.languageCode;
      const waitChanged = this.settings.waitPerBeadMs !== newWaitPerBeadMs;

      if (!languageChanged && !waitChanged) {
        return;
      }

      this.settings.languageCode = result.languageCode || this.settings.languageCode;
      this.settings.waitPerBeadMs = newWaitPerBeadMs;
      this.saveReaderSettings();
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  getBeadClasses(paletteTypeId: number): string {
    return Utility.getBeadClasses(paletteTypeId);
  }

  upgrade(): void {
    this.dialogRef.close({ action: "subscribe" });
  }

  get currentRow(): WordChartRow | null {
    return this.rows[this.currentRowIndex] || null;
  }

  get currentSegments(): BeadSegment[] {
    return this.currentRow?.segments || [];
  }

  get canPlay(): boolean {
    return !this.isPreviewMode && this.ttsSupported && this.rows.length > 0;
  }

  get currentLanguageCode(): string {
    return this.settings.languageCode || this.getDefaultLanguageCode();
  }

  get hiddenPreviewRowsCount(): number {
    if (!this.isPreviewMode) return 0;
    return Math.max(this.totalRowsCount - this.rows.length, 0);
  }

  async onPlay(): Promise<void> {
    if (!this.canPlay) return;

    if (this.isPaused) {
      this.tts.resume();
      this.tts.resumeCountdown();
      this.isPaused = false;
      return;
    }

    this.isPlaying = true;
    this.isPaused = false;
    const row = this.currentRow;
    let startSegmentIndex = this.currentSegmentIndex;
    if (!row || startSegmentIndex < 0 || startSegmentIndex >= row.segments.length) {
      startSegmentIndex = 0;
    }
    this.scrollToCurrentRow();
    this.queueProgressSave();
    await this.speakSegment(startSegmentIndex, true);
  }

  onPause(): void {
    if (!this.canPlay) return;
    this.tts.pause();
    this.tts.pauseCountdown();
    this.isPaused = true;
  }

  onStop(): void {
    this.tts.stop();
    this.isPlaying = false;
    this.isPaused = false;
    this.currentSegmentIndex = -1;
    this.waitCountdown = 0;
    this.queueProgressSave();
  }

  async onNextSegment(): Promise<void> {
    if (!this.currentRow) return;
    const wasPlaying = this.isPlaying;
    const nextIndex = this.currentSegmentIndex + 1;

    if (nextIndex < this.currentRow.segments.length) {
      if (wasPlaying) {
        this.onStop();
        setTimeout(() => {
          this.isPlaying = true;
          this.speakSegment(nextIndex, false);
        }, 100);
      } else {
        this.currentSegmentIndex = nextIndex;
        this.scrollToCurrentRow();
        this.queueProgressSave();
      }
    } else {
      this.onStop();
      if (this.currentRowIndex < this.rows.length - 1) {
        this.currentRowIndex++;
        this.scrollToCurrentRow();
        this.queueProgressSave();
        if (wasPlaying) {
          setTimeout(() => {
            this.isPlaying = true;
            this.speakSegment(0, true);
          }, 100);
        }
      }
    }
  }

  async onPrevSegment(): Promise<void> {
    if (!this.currentRow) return;
    const wasPlaying = this.isPlaying;
    const prevIndex = this.currentSegmentIndex - 1;

    if (prevIndex >= 0) {
      if (wasPlaying) {
        this.onStop();
        setTimeout(() => {
          this.isPlaying = true;
          this.speakSegment(prevIndex, false);
        }, 100);
      } else {
        this.currentSegmentIndex = prevIndex;
        this.scrollToCurrentRow();
        this.queueProgressSave();
      }
    } else if (this.currentRowIndex > 0) {
      this.onStop();
      this.currentRowIndex--;
      this.scrollToCurrentRow();
      const previousRow = this.rows[this.currentRowIndex];
      const lastSegment = previousRow.segments.length - 1;
      this.currentSegmentIndex = lastSegment;
      this.queueProgressSave();
      if (wasPlaying) {
        setTimeout(() => {
          this.isPlaying = true;
          this.speakSegment(lastSegment, false);
        }, 100);
      }
    }
  }

  onReset(): void {
    this.onStop();
    this.currentRowIndex = 0;
    this.currentSegmentIndex = -1;
    this.scrollToCurrentRow();
    this.queueProgressSave();
  }

  onSkipWait(): void {
    this.tts.skipWait();
    this.waitCountdown = 0;
  }

  onSegmentClick(rowIndex: number, segmentIndex: number): void {
    if (!this.canPlay) {
      this.currentRowIndex = rowIndex;
      this.currentSegmentIndex = segmentIndex;
      this.scrollToCurrentRow();
      this.queueProgressSave();
      return;
    }

    this.tts.stop();
    this.currentRowIndex = rowIndex;
    this.isPlaying = true;
    this.isPaused = false;
    this.currentSegmentIndex = segmentIndex;
    this.scrollToCurrentRow();
    this.queueProgressSave();

    const row = this.rows[rowIndex];
    const segment = row?.segments[segmentIndex];
    if (!segment) return;

    const speech = this.buildSegmentSpeechText(segment);
    const waitTime = segment.count * this.settings.waitPerBeadMs;

    this.tts.speak(
      speech,
      1.0,
      this.currentLanguageCode,
      () => {
        if (!this.isPlaying) return;
        const nextIndex = segmentIndex + 1;
        if (nextIndex < row.segments.length) {
          this.onSegmentClick(rowIndex, nextIndex);
        } else {
          this.currentSegmentIndex = -1;
          this.isPlaying = false;
        }
      },
      () => {
        this.currentSegmentIndex = -1;
        this.isPlaying = false;
      },
      waitTime,
      (remaining) => (this.waitCountdown = remaining),
    );
  }

  onRowHeaderClick(rowIndex: number): void {
    this.currentRowIndex = rowIndex;
    this.scrollToCurrentRow();
    this.queueProgressSave();
  }

  isActiveSegment(rowIndex: number, segmentIndex: number): boolean {
    return rowIndex === this.currentRowIndex &&
      segmentIndex === this.currentSegmentIndex;
  }

  buildSegmentSpeechText(segment: BeadSegment): string {
    const letterName = this.tts.getPhoneticLetter(
      segment.letter,
      this.currentLanguageCode,
    );
    return `${segment.count} ${letterName}`;
  }

  async speakSegment(segmentIndex: number, _isFirstSegment: boolean): Promise<void> {
    const row = this.currentRow;
    if (!row || !this.canPlay) return;

    const segment = row.segments[segmentIndex];
    if (!segment) return;

    this.currentSegmentIndex = segmentIndex;
    this.scrollToCurrentRow();
    this.queueProgressSave();

    const speech = this.buildSegmentSpeechText(segment);
    const waitTime = segment.count * this.settings.waitPerBeadMs;

    this.tts.speak(
      speech,
      1.0,
      this.currentLanguageCode,
      () => {
        if (!this.isPlaying || this.isPaused) return;

        const nextSegment = segmentIndex + 1;
        if (nextSegment < row.segments.length) {
          this.speakSegment(nextSegment, false);
        } else if (this.currentRowIndex < this.rows.length - 1) {
          this.currentSegmentIndex = -1;
          this.currentRowIndex++;
          this.scrollToCurrentRow();
          this.queueProgressSave();
          setTimeout(() => {
            if (this.isPlaying && !this.isPaused) {
              this.speakSegment(0, false);
            }
          }, 300);
        } else {
          this.currentSegmentIndex = -1;
          this.isPlaying = false;
          this.isPaused = false;
          this.queueProgressSave();
        }
      },
      () => {
        this.currentSegmentIndex = -1;
        this.isPlaying = false;
        this.isPaused = false;
        this.queueProgressSave();
      },
      waitTime,
      (remaining) => (this.waitCountdown = remaining),
    );
  }

  onKeyDown(event: KeyboardEvent): void {
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    ) {
      return;
    }

    switch (event.key) {
      case " ":
        event.preventDefault();
        if (this.isPlaying && !this.isPaused) {
          this.onPause();
        } else {
          this.onPlay();
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        this.onNextSegment();
        break;
      case "ArrowLeft":
        event.preventDefault();
        this.onPrevSegment();
        break;
      case "Escape":
        event.preventDefault();
        this.onStop();
        break;
      case "Home":
        event.preventDefault();
        this.onReset();
        break;
      case "Enter":
        event.preventDefault();
        if (this.waitCountdown > 0) {
          this.onSkipWait();
        }
        break;
    }
  }

  onLanguageChange(value: string): void {
    this.settings.languageCode = value;
    this.saveReaderSettings();
  }

  onWaitPerBeadChange(value: number | null): void {
    if (value == null) return;
    this.settings.waitPerBeadMs = this.normalizeWaitPerBeadMs(value);
    this.saveReaderSettings();
  }

  getRowProgressLabel(): string {
    if (!this.rows.length) return "";
    return `${this.currentRowIndex + 1} / ${this.rows.length}`;
  }

  getCountdownSeconds(): number {
    return Math.ceil(this.waitCountdown / 1000);
  }

  getTimeOptionLabel(value: number): string {
    return this.timeOptionLabels.get(value) || `${value / 1000}s`;
  }

  private normalizeWaitPerBeadMs(value: number): number {
    const min = this.timeOptions[0];
    const max = this.timeOptions[this.timeOptions.length - 1];
    const clamped = Math.min(Math.max(Number(value) || min, min), max);
    const snapped = Math.round((clamped - min) / this.waitPerBeadStepMs) * this.waitPerBeadStepMs + min;
    return Math.min(Math.max(snapped, min), max);
  }

  private loadReaderSettings(): ReaderSettings {
    const raw = this.httpService?.user?.readerSettings;
    if (!raw) {
      return {
        ...DEFAULT_READER_SETTINGS,
        languageCode: this.getDefaultLanguageCode(),
      };
    }

    try {
      const parsed = JSON.parse(raw) as ReaderSettings;
      const progressByPatternKey = parsed.progressByPatternKey && typeof parsed.progressByPatternKey === "object"
        ? parsed.progressByPatternKey
        : {};
      return {
        ...DEFAULT_READER_SETTINGS,
        ...parsed,
        progressByPatternKey,
        languageCode: parsed.languageCode || this.getDefaultLanguageCode(),
      };
    } catch {
      return {
        ...DEFAULT_READER_SETTINGS,
        languageCode: this.getDefaultLanguageCode(),
      };
    }
  }

  private restoreProgressFromSettings(): void {
    const currentPatternKey = this.getCurrentPatternRestoreKey();
    const perPatternProgress = this.getProgressForPatternKey(currentPatternKey);
    if (perPatternProgress) {
      this.applySavedProgress(perPatternProgress.rowIndex, perPatternProgress.segmentIndex, false);
      this.hasPendingInitialRestoreScroll = true;
      return;
    }

    if (this.settings.lastPatternKey) {
      if (this.settings.lastPatternKey !== currentPatternKey) return;
    } else {
      const hasSavedPatternId = Number.isInteger(this.settings.lastPatternId) && (this.settings.lastPatternId as number) > 0;
      const hasCurrentPatternId = Number.isInteger(this.data.patternId) && (this.data.patternId as number) > 0;

      if (hasSavedPatternId && hasCurrentPatternId) {
        if (this.settings.lastPatternId !== this.data.patternId) return;
      } else {
        if (!this.settings.lastPatternName) return;
        if (this.settings.lastPatternName !== this.data.patternName) return;
      }
    }

    const rowIndex = Number.isInteger(this.settings.lastRowIndex)
      ? (this.settings.lastRowIndex as number)
      : 0;
    const segmentIndex = Number.isInteger(this.settings.lastSegmentIndex)
      ? (this.settings.lastSegmentIndex as number)
      : -1;
    this.applySavedProgress(rowIndex, segmentIndex, false);
    this.hasPendingInitialRestoreScroll = true;
  }

  private getDefaultLanguageCode(): string {
    const language = this.httpService?.user?.language;
    switch (language) {
      case LanguageEnum.Italian:
        return "it-IT";
      case LanguageEnum.German:
        return "de-DE";
      case LanguageEnum.French:
        return "fr-FR";
      case LanguageEnum.Spanish:
        return "es-ES";
      case LanguageEnum.Dutch:
        return "nl-NL";
      case LanguageEnum.Portuguese:
        return "pt-PT";
      case LanguageEnum.Polish:
        return "pl-PL";
      case LanguageEnum.English:
      default:
        return "en-US";
    }
  }

  private saveReaderSettings(): void {
    if (!this.httpService.user) return;

    const newSettings = JSON.stringify(this.settings);
    if (!this.isSavingSettings && this.httpService.user.readerSettings === newSettings) {
      // Settings unchanged, skip API call
      return;
    }

    // Keep local model updated even when we defer network persistence.
    this.httpService.user.readerSettings = newSettings;

    if (this.isSavingSettings) {
      this.hasPendingSettingsSave = true;
      return;
    }

    this.isSavingSettings = true;
    const savedSnapshot = newSettings;
    this.httpService.updateUserProfile()
      .catch(() => {
        // Non-blocking for reader flow
      })
      .finally(() => {
        this.isSavingSettings = false;
        if (this.hasPendingSettingsSave) {
          this.hasPendingSettingsSave = false;
          const latestSettings = JSON.stringify(this.settings);
          if (latestSettings !== savedSnapshot) {
            this.saveReaderSettings();
          }
        }
      });
  }

  private queueProgressSave(): void {
    if (!this.httpService.user) return;
    if (this.progressSaveTimer !== null) {
      window.clearTimeout(this.progressSaveTimer);
    }
    this.progressSaveTimer = window.setTimeout(() => {
      this.progressSaveTimer = null;
      this.persistProgressNow();
    }, 1500);
  }

  private flushProgressSave(): void {
    if (this.progressSaveTimer !== null) {
      window.clearTimeout(this.progressSaveTimer);
      this.progressSaveTimer = null;
      this.persistProgressNow();
    }
  }

  private persistProgressNow(): void {
    const currentPatternKey = this.getCurrentPatternRestoreKey();
    this.settings.lastPatternKey = currentPatternKey;
    this.settings.lastPatternId = this.data.patternId || 0;
    this.settings.lastPatternName = this.data.patternName;
    this.settings.lastRowIndex = this.currentRowIndex;
    this.settings.lastSegmentIndex = this.currentSegmentIndex;
    this.saveProgressForPatternKey(currentPatternKey, this.currentRowIndex, this.currentSegmentIndex);
    this.saveReaderSettings();
  }

  private getCurrentPatternRestoreKey(): string {
    const patternId = Number(this.data.patternId) || 0;
    if (patternId > 0) {
      return `id:${patternId}`;
    }

    const normalizedName = (this.data.patternName || "").trim().toLowerCase();
    const rowCount = this.rows.length;
    let segmentCount = 0;
    let beadCount = 0;

    for (const row of this.rows) {
      segmentCount += row.segments.length;
      for (const segment of row.segments) {
        beadCount += segment.count;
      }
    }

    return `unsaved:${normalizedName}:${rowCount}:${segmentCount}:${beadCount}`;
  }

  private getProgressForPatternKey(patternKey: string): { rowIndex: number; segmentIndex: number } | null {
    if (!patternKey) return null;
    if (!this.settings.progressByPatternKey) return null;
    const progress = this.settings.progressByPatternKey[patternKey];
    if (!progress) return null;

    const rowIndex = Number.isInteger(progress.rowIndex) ? progress.rowIndex : 0;
    const segmentIndex = Number.isInteger(progress.segmentIndex) ? progress.segmentIndex : -1;
    return { rowIndex, segmentIndex };
  }

  private saveProgressForPatternKey(patternKey: string, rowIndex: number, segmentIndex: number): void {
    if (!patternKey) return;
    if (!this.settings.progressByPatternKey) {
      this.settings.progressByPatternKey = {};
    }

    this.settings.progressByPatternKey[patternKey] = {
      rowIndex,
      segmentIndex,
      updatedAt: Date.now(),
    };

    const entries = Object.entries(this.settings.progressByPatternKey);
    if (entries.length <= MAX_READER_PROGRESS_ENTRIES) return;

    entries.sort((a, b) => (b[1]?.updatedAt || 0) - (a[1]?.updatedAt || 0));
    const trimmedProgress: ReaderSettings["progressByPatternKey"] = {};
    entries.slice(0, MAX_READER_PROGRESS_ENTRIES).forEach(([key, value]) => {
      trimmedProgress[key] = value;
    });
    this.settings.progressByPatternKey = trimmedProgress;
  }

  private applySavedProgress(rowIndex: number, segmentIndex: number, shouldScroll: boolean = true): void {
    const clampedRowIndex = Math.min(
      Math.max(rowIndex, 0),
      Math.max(this.rows.length - 1, 0),
    );

    const row = this.rows[clampedRowIndex];
    const maxSegment = row ? row.segments.length - 1 : -1;
    const clampedSegmentIndex = Math.min(Math.max(segmentIndex, -1), maxSegment);

    this.currentRowIndex = clampedRowIndex;
    this.currentSegmentIndex = clampedSegmentIndex;
    if (shouldScroll) {
      this.scrollToCurrentRow();
    }
  }

  private scrollToCurrentRow(): void {
    if (!this.rowsContainer || !this.rowElements) return;

    // If we have a specific segment selected, scroll to it instead of just the row
    if (this.currentSegmentIndex >= 0 && this.segmentElements) {
      const segmentElementsArray = this.segmentElements.toArray();

      // Calculate the flat index for the current segment
      let segmentFlatIndex = 0;
      for (let i = 0; i < this.currentRowIndex; i++) {
        segmentFlatIndex += this.rows[i]?.segments.length || 0;
      }
      segmentFlatIndex += this.currentSegmentIndex;

      if (segmentFlatIndex >= 0 && segmentFlatIndex < segmentElementsArray.length) {
        const segmentElement = segmentElementsArray[segmentFlatIndex];
        if (segmentElement?.nativeElement) {
          segmentElement.nativeElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
          return;
        }
      }
    }

    // Fallback to scrolling to the row if no segment is selected
    const rowElementsArray = this.rowElements.toArray();
    if (this.currentRowIndex >= 0 && this.currentRowIndex < rowElementsArray.length) {
      const currentRowElement = rowElementsArray[this.currentRowIndex];
      if (currentRowElement?.nativeElement) {
        currentRowElement.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }
  }
}
